/**
 * =============================================================================
 * FALCON - Module Deneigement des Parkings Avion
 * =============================================================================
 *
 * @file        twoFactorService.ts
 * @description Service API pour la gestion de la double authentification (2FA)
 *              Fournit les methodes pour configurer, verifier et desactiver le TOTP
 *
 * @version     2.5
 * @author      Equipe FALCON
 * =============================================================================
 */

import apiClient from './api';

/**
 * Reponse de la configuration initiale 2FA
 */
export interface TwoFactorSetupResponse {
  /** Secret TOTP (pour affichage manuel) */
  secret: string;
  /** URL pour generer le QR code */
  qrCodeUrl: string;
  /** Codes de secours a usage unique */
  backupCodes: string[];
}

/**
 * Reponse de verification 2FA
 */
export interface TwoFactorVerifyResponse {
  message: string;
  enabled: boolean;
}

/**
 * Statut 2FA de l'utilisateur
 */
export interface TwoFactorStatusResponse {
  enabled: boolean;
}

/**
 * Reponse de desactivation 2FA
 */
export interface TwoFactorDisableResponse {
  message: string;
}

/**
 * Reponse de regeneration des codes de secours
 */
export interface BackupCodesResponse {
  backupCodes: string[];
}

/**
 * Service de gestion de la double authentification TOTP
 */
export const twoFactorService = {
  /**
   * Recupere le statut actuel de la 2FA pour l'utilisateur connecte
   * @returns L'etat d'activation de la 2FA
   */
  getStatus: async (): Promise<TwoFactorStatusResponse> => {
    const response = await apiClient.get<TwoFactorStatusResponse>('/2fa/status');
    return response.data;
  },

  /**
   * Initialise la configuration 2FA
   * Genere un nouveau secret TOTP et les codes de secours
   *
   * @returns Le secret, l'URL du QR code et les codes de secours
   */
  setup: async (): Promise<TwoFactorSetupResponse> => {
    const response = await apiClient.post<TwoFactorSetupResponse>('/2fa/setup');
    return response.data;
  },

  /**
   * Verifie le code OTP et active la 2FA
   * A appeler apres que l'utilisateur a scanne le QR code
   *
   * @param code Le code TOTP a 6 chiffres
   * @returns Confirmation de l'activation
   */
  verify: async (code: string): Promise<TwoFactorVerifyResponse> => {
    const response = await apiClient.post<TwoFactorVerifyResponse>('/2fa/verify', { code });
    return response.data;
  },

  /**
   * Desactive la double authentification
   * Requiert le mot de passe de l'utilisateur pour confirmation
   *
   * @param password Le mot de passe de l'utilisateur
   * @returns Confirmation de la desactivation
   */
  disable: async (password: string): Promise<TwoFactorDisableResponse> => {
    const response = await apiClient.post<TwoFactorDisableResponse>('/2fa/disable', { password });
    return response.data;
  },

  /**
   * Regenere les codes de secours
   * Les anciens codes deviennent invalides
   *
   * @returns Les nouveaux codes de secours
   */
  regenerateBackupCodes: async (): Promise<BackupCodesResponse> => {
    const response = await apiClient.post<BackupCodesResponse>('/2fa/backup-codes');
    return response.data;
  },

  /**
   * Valide un code OTP lors du login
   * Appele apres la premiere etape du login si 2FA est active
   *
   * @param tempToken Token temporaire recu lors du login initial
   * @param code Code TOTP a 6 chiffres ou code de secours
   * @returns Le token JWT final si valide
   */
  validateLogin: async (
    tempToken: string,
    code: string
  ): Promise<{ token: string }> => {
    const response = await apiClient.post<{ token: string }>('/login/2fa', {
      tempToken,
      code,
    });
    return response.data;
  },
};

export default twoFactorService;
